import React, { useEffect, useMemo } from "react";

import {
  BoltIcon,
  ChevronRightIcon,
  ClipboardDocumentListIcon,
  CreditCardIcon,
  CurrencyDollarIcon,
} from "@heroicons/react/24/outline";
import moment from "moment-timezone";
import { useNavigate } from "react-router-dom";

import {
  ROUTE_ARTICLES,
  ROUTE_PLANS,
  ROUTE_PROFILE,
} from "../../constants/routes";
import useSelectedAccount from "../../hooks/useSelectedAccount";
import { useGetAllArticlesQuery } from "../../redux/services/article";
import { useGetDashboardQuery } from "../../redux/services/dashboard";
import { useGetSubscriptionQuery } from "../../redux/services/subscription";
import { formatDateUS, formatNumberWithCommas, getDaysLeft } from "../../utils";

import BarChart from "../../components/charts/BarChart";
import PageHeading from "../../components/common/PageHeading";
import SpringContainer from "../../components/containers/SpringContainer";
import Button from "../../components/form/Button";
import LoadingSpinner from "../../components/loaders/LoadingSpinner";
import ArticleTableContainer from "../Articles/components/ArticleTableContainer";
import { MetricCard } from "./components/MetricCard";

const Dashboard = () => {
  const navigate = useNavigate();
  const { accountId, timezone } = useSelectedAccount();

  const { data: dashboard, isLoading: isLoadingDashboard } =
    useGetDashboardQuery(
      {
        accountId,
        timezone,
      },
      { skip: !accountId }
    );

  const {
    data: subscription,
    isLoading: isLoadingSubscription,
    isUninitialized: isUninitializedSubscription,
  } = useGetSubscriptionQuery(
    {
      accountId,
    },
    { skip: !accountId }
  );

  const {
    data: articles,
    refetch: refetchArticles,
    isFetching: isFetchingArticles,
  } = useGetAllArticlesQuery(
    {
      accountId,
      offset: 0,
      limit: 3,
      includeTotalCount: true,
    },
    { skip: !accountId }
  );

  useEffect(() => {
    if (
      !isLoadingSubscription &&
      !isUninitializedSubscription &&
      !subscription
    ) {
      navigate(ROUTE_PLANS);
    }
  }, [
    isLoadingSubscription,
    isUninitializedSubscription,
    subscription,
    navigate,
  ]);

  const remainingSubscriptionDays = useMemo(() => {
    const endDate = moment(dashboard?.currentPeriodEnd);
    const currentDate = moment().tz(dashboard?.timezone);
    return getDaysLeft(endDate, currentDate);
  }, [dashboard?.currentPeriodEnd, dashboard?.timezone]);

  if (isLoadingDashboard || !dashboard) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <LoadingSpinner size="32px" />
      </div>
    );
  }

  // plan metric card
  const planData = (
    <div className="flex items-center gap-1 flex-wrap">
      <span>{dashboard?.planName}</span>
      <span className="capitalize bg-green-200/50 p-0.5 px-2 rounded-sm text-xs text-green-900">
        {dashboard?.status}
      </span>
    </div>
  );

  const planHeading = (
    <div className="flex gap-1 flex-wrap">
      <span>{formatDateUS(dashboard?.currentPeriodStart, false)}</span>
      <span>-</span>
      <span>{formatDateUS(dashboard?.currentPeriodEnd)}</span>
      <span>
        ({remainingSubscriptionDays >= 0 ? remainingSubscriptionDays : 0}{" "}
        {remainingSubscriptionDays > 1 ? "days" : "day"} left)
      </span>
    </div>
  );
  // plan metric card

  return (
    <SpringContainer className="overflow-y-auto overflow-x-hidden">
      <PageHeading heading="Overview" />
      <div className="my-4 grid grid-cols-1 md:grid-cols-2 gap-5 xl:grid-cols-4">
        <MetricCard
          icon={<CreditCardIcon className="size-6 text-blue-800" />}
          iconBackground="bg-blue-100/50"
          data={planData}
          heading={planHeading}
        />
        <MetricCard
          icon={<CurrencyDollarIcon className="size-6 text-amber-500" />}
          iconBackground="bg-amber-100/50"
          heading="Tokens Remaining"
          data={formatNumberWithCommas(dashboard?.tokensRemaining)}
        />
        <MetricCard
          icon={<ClipboardDocumentListIcon className="size-6 text-red-800" />}
          iconBackground="bg-red-100/50"
          heading="Articles"
          data={dashboard.articlesThisPeriod}
        />
        <MetricCard
          icon={<BoltIcon className="size-6 text-green-800" />}
          iconBackground="bg-green-100/50"
          heading="Active Automations"
          data={dashboard.activeAutomations}
        />
      </div>
      <div className="border border-gray-200 rounded-lg p-2 w-full">
        <div className="flex justify-between items-center flex-wrap">
          <h2 className="ml-2 mt-2 text-base lg:text-lg">
            Tokens used by billing period month
          </h2>
          <div className="flex flex-col mx-2">
            <div className="text-2xs uppercase text-gray-500 tracking-wide font-semibold">
              Timezone
            </div>
            <Button
              paddingZero
              variant="transparent-primary"
              onClick={() => navigate(ROUTE_PROFILE)}
            >
              {dashboard?.timezone}
            </Button>
          </div>
        </div>
        <BarChart data={dashboard?.tokenUsage} />
        <div className="mb-4 text-gray-500 text-sm flex items-center gap-1 justify-center">
          <div className="rounded-sm w-3 h-3 bg-[#31C471]" />
          {`${formatNumberWithCommas(dashboard?.tokensUsed ?? 0)} token${dashboard?.tokensUsed > 1 ? "s" : ""} used`}
        </div>
      </div>
      {articles?.data?.length > 0 && (
        <div className="w-full mt-4 flex flex-col gap-2">
          <PageHeading
            heading="Recent Articles"
            tooltip="Refresh"
            loading={isFetchingArticles}
            refetch={refetchArticles}
          />
          <div>
            <ArticleTableContainer articles={articles.data} />
            <div className="flex justify-end mt-2">
              <Button
                variant="transparent-primary"
                className="flex items-center gap-1"
                onClick={() => navigate(ROUTE_ARTICLES)}
              >
                See all articles <ChevronRightIcon className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </SpringContainer>
  );
};

export default React.memo(Dashboard);
